import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

const CORES_STATUS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#757575'];

const formatMoeda = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

export default function PrestadorGraficosPanel({ dados, loading }) {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!dados) return null;

  const porMes = (dados.porMes || []).map((m) => ({
    ...m,
    label: m.competencia?.slice(5, 7) + '/' + m.competencia?.slice(0, 4)
  }));
  const porStatus = dados.porStatus || [];
  const porContrato = (dados.porContrato || []).slice(0, 12);

  const semDados = !porMes.length && !porStatus.length;

  if (semDados) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography color="text.secondary" variant="body2">
          Gráficos serão exibidos quando houver notas fiscais lançadas nos seus contratos.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        Indicadores e gráficos
      </Typography>
      <Grid container spacing={2}>
        {porMes.length > 0 && (
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, borderRadius: 2, height: 340 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Valores por competência (bruto)
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={porMes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatMoeda(v)} />
                  <Bar dataKey="bruto" name="Bruto" fill="#1976d2" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="liquido" name="Líquido" fill="#2e7d32" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}
        {porStatus.length > 0 && (
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 2, height: 340 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Notas por status
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={porStatus}
                    dataKey="quantidade"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ status, quantidade }) => `${status}: ${quantidade}`}
                  >
                    {porStatus.map((_, i) => (
                      <Cell key={i} fill={CORES_STATUS[i % CORES_STATUS.length]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}
        {porContrato.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 2, height: 320 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Valor bruto por contrato
              </Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={porContrato} layout="vertical" margin={{ left: 120 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => formatMoeda(v)} />
                  <YAxis type="category" dataKey="numeroContrato" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatMoeda(v)} />
                  <Bar dataKey="valorBruto" name="Bruto" fill="#42a5f5" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
